/**
 * 我的挂牌（待审核/在售/已下架/已售）
 * 编辑/下架/再上架；审核状态流转 IR-16
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listMyListings, offlineListing, onlineListing } from '@/api/listing';
import { listMyAuctions } from '@/api/auction';
import type { Auction, Listing } from '@/types';
import { formatDateTime, formatMoney, formatWeight } from '@/utils/format';
import { imgFallback } from '@/utils/constants';
import StatusTag from '@/components/common/StatusTag';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/listing.css';

const TABS = [
  { key: 'all', label: '全部', status: undefined },
  { key: 'pending', label: '待审核', status: 0 },
  { key: 'onsale', label: '在售', status: 1 },
  { key: 'offline', label: '已下架', status: 2 },
  { key: 'sold', label: '已售', status: 3 },
  { key: 'auction', label: '我的竞价', status: undefined },
];

export default function MyListings() {
  const [tab, setTab] = useState('all');
  const [list, setList] = useState<Listing[]>([]);
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  // 合并两源（架构 §11-#1）：一口价挂牌（/listings/mine）+ 我的竞价场次（/auctions/mine）
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, ar] = await Promise.allSettled([listMyListings(), listMyAuctions()]);
      const listings = lr.status === 'fulfilled' ? lr.value : [];
      const mine = ar.status === 'fulfilled' ? ar.value : [];
      setList(listings);
      setAuctions(mine);
      // 竞价源失败仅静默跳过；挂牌源失败才透出错误
      setError(
        lr.status === 'rejected'
          ? lr.reason instanceof Error
            ? lr.reason.message
            : '加载失败'
          : '',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = tab === 'all' ? list : list.filter((l) => l.status === TABS.find((t) => t.key === tab)?.status);

  const handleOffline = async (id: number) => {
    setBusyId(id);
    try {
      await offlineListing(id);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleOnline = async (id: number) => {
    setBusyId(id);
    try {
      await onlineListing(id);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="listing-page">
      <PageHero eyebrow="MY LISTINGS" title="我的挂牌" desc="管理您的牛只挂牌，跟踪审核与销售状态">
        <Link className="btn btn-gold btn-sm" to="/listing/new">＋ 发布挂牌</Link>
      </PageHero>

      <div className="container">
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`tab-item ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Loading rows={5} type="list" />
        ) : error ? (
          <ErrorBox message={error} onRetry={load} />
        ) : tab === 'auction' ? (
          auctions.length === 0 ? (
            <Empty icon="🔨" title="暂无竞价场次" desc="发布竞价挂牌，开启英式拍卖" action={<Link className="btn btn-primary btn-sm" to="/listing/new">去发布</Link>} />
          ) : (
            <div className="listing-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>牛只</th>
                    <th>交易方式</th>
                    <th>当前价</th>
                    <th>出价</th>
                    <th>状态</th>
                    <th>结束时间</th>
                    <th className="ops">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {auctions.map((a) => {
                    const c = a.cattle;
                    const cover = c?.images?.[0] ?? c?.face_image_url;
                    return (
                      <tr key={a.id}>
                        <td>
                          <Link className="listing-cell" to={`/auction/${a.id}`}>
                            <span className="listing-cell__thumb">{cover ? <img src={cover} alt="" data-local="/images/cattle-default.jpg" onError={imgFallback} /> : '🐂'}</span>
                            <span>
                              <strong>{c?.breed} · {c?.ear_tag_no}</strong>
                              <em>{formatWeight(c?.weight)} · {c?.age_days}日</em>
                            </span>
                          </Link>
                        </td>
                        <td><StatusTag type="trade" status={2} /></td>
                        <td className="num">{formatMoney(a.current_price)}</td>
                        <td>{a.bid_count} 次</td>
                        <td><StatusTag type="auction" status={a.status} /></td>
                        <td>{formatDateTime(a.end_time)}</td>
                        <td className="ops">
                          <Link className="btn btn-ghost btn-sm" to={`/auction/${a.id}`}>查看</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : filtered.length === 0 ? (
          <Empty icon="🏷️" title="暂无挂牌" desc="发布挂牌，让更多买家看到您的牛只" action={<Link className="btn btn-primary btn-sm" to="/listing/new">去发布</Link>} />
        ) : (
          <div className="listing-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>牛只</th>
                  <th>交易方式</th>
                  <th>价格</th>
                  <th>数量</th>
                  <th>状态</th>
                  <th>发布时间</th>
                  <th className="ops">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const c = l.cattle;
                  const cover = c?.images?.[0] ?? c?.face_image_url;
                  return (
                    <tr key={l.id}>
                      <td>
                        <Link className="listing-cell" to={`/cattle/${l.cattle_id}`}>
                          <span className="listing-cell__thumb">{cover ? <img src={cover} alt="" data-local="/images/cattle-default.jpg" onError={imgFallback} /> : '🐂'}</span>
                          <span>
                            <strong>{c?.breed} · {c?.ear_tag_no}</strong>
                            <em>{formatWeight(c?.weight)} · {c?.age_days}日</em>
                          </span>
                        </Link>
                      </td>
                      <td><StatusTag type="trade" status={l.trade_mode} /></td>
                      <td className="num">{formatMoney(l.price)}</td>
                      <td>{l.available_qty} 头</td>
                      <td>
                        <StatusTag type="listing" status={l.status} />
                        {l.status === 4 && l.reject_reason ? <div className="listing-reject">{l.reject_reason}</div> : null}
                      </td>
                      <td>{formatDateTime(l.created_at)}</td>
                      <td className="ops">
                        <Link className="btn btn-ghost btn-sm" to={`/cattle/${l.cattle_id}`}>查看</Link>
                        {l.status === 1 ? (
                          <button className="btn btn-outline btn-sm" disabled={busyId === l.id} onClick={() => handleOffline(l.id)}>
                            下架
                          </button>
                        ) : null}
                        {l.status === 2 ? (
                          <button className="btn btn-outline btn-sm" disabled={busyId === l.id} onClick={() => handleOnline(l.id)}>
                            再上架
                          </button>
                        ) : null}
                        {l.status === 0 || l.status === 4 ? (
                          <Link className="btn btn-outline btn-sm" to={`/listing/new?id=${l.id}`}>编辑</Link>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
